/**
 * Billing Tenants Page (Super Admin)
 * Lista todos os tenants com filtros e acoes
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, Button, Input, useToast, ConfirmDialog } from "@payflow/ui";

const API_BASE = (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";
const TOKEN_KEY = "payflow_token";

interface TenantRow {
  id: string;
  slug: string;
  nome: string;
  documento: string;
  email: string;
  plano: string;
  status: string;
  subscriptionAmount: number | null;
  nextBillingDate: string | null;
  createdAt: string;
}

export function BillingTenantsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [actionTarget, setActionTarget] = useState<{ tenant: TenantRow; action: "suspend" | "reactivate" } | null>(null);
  const [acting, setActing] = useState(false);
  const [resendTarget, setResendTarget] = useState<TenantRow | null>(null);
  const [resending, setResending] = useState(false);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem(TOKEN_KEY);
    const params = new URLSearchParams({ page: String(page), limit: "20" });
    if (search) params.set("search", search);
    if (statusFilter) params.set("status", statusFilter);

    try {
      const res = await fetch(`${API_BASE}/v1/admin/billing/tenants?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setTenants(data.data || []);
        setTotal(data.meta?.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleAction = async () => {
    if (!actionTarget) return;
    setActing(true);
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(`${API_BASE}/v1/admin/billing/tenants/${actionTarget.tenant.id}/${actionTarget.action}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) {
        toast({
          variant: "success",
          title: actionTarget.action === "suspend"
            ? `"${actionTarget.tenant.nome}" suspenso`
            : `"${actionTarget.tenant.nome}" reativado`,
        });
        fetchTenants();
      } else {
        toast({ variant: "error", title: "Erro ao executar acao" });
      }
    } catch {
      toast({ variant: "error", title: "Erro de conexao" });
    } finally {
      setActing(false);
      setActionTarget(null);
    }
  };

  const handleResendCredentials = async () => {
    if (!resendTarget) return;
    setResending(true);
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const res = await fetch(`${API_BASE}/v1/admin/billing/tenants/${resendTarget.id}/resend-credentials`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.success) {
        const d = json.data;
        const channels: string[] = [];
        if (d?.emailSent) channels.push("Email");
        if (d?.whatsappSent) channels.push("WhatsApp");

        if (channels.length > 0) {
          toast({
            variant: "success",
            title: `Credenciais enviadas via ${channels.join(" e ")}`,
          });
        } else {
          const errors: string[] = [];
          if (d?.emailError) errors.push(`Email: ${d.emailError}`);
          if (d?.whatsappError) errors.push(`WhatsApp: ${d.whatsappError}`);
          toast({
            variant: "warning",
            title: "Senha resetada, mas falha no envio",
            description: errors.join(". "),
          });
        }
      } else {
        toast({ variant: "error", title: json.error?.message || "Erro ao reenviar credenciais" });
      }
    } catch {
      toast({ variant: "error", title: "Erro de conexao" });
    } finally {
      setResending(false);
      setResendTarget(null);
    }
  };

  const statusColor = (s: string) => {
    switch (s) {
      case "active": return "bg-green-100 text-green-700";
      case "suspended": return "bg-red-100 text-red-700";
      case "trial": return "bg-blue-100 text-blue-700";
      case "cancelled": return "bg-gray-100 text-gray-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Tenants</h1>
        <Button variant="outline" onClick={() => navigate("/gestor")}>
          Voltar ao Dashboard
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="flex-1">
          <Input
            placeholder="Buscar por nome, CNPJ ou email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="">Todos os status</option>
          <option value="active">Ativo</option>
          <option value="suspended">Suspenso</option>
          <option value="trial">Trial</option>
          <option value="cancelled">Cancelado</option>
        </select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Empresa</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">CNPJ</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Plano</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Valor</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Carregando...</td></tr>
                ) : tenants.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Nenhum tenant encontrado</td></tr>
                ) : (
                  tenants.map((t) => (
                    <tr key={t.id} className="border-b border-border hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/gestor/tenants/${t.id}`)}
                          className="font-medium text-foreground hover:text-primary-600 transition-colors"
                        >
                          {t.nome}
                        </button>
                        <p className="text-xs text-muted-foreground">{t.email}</p>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{t.documento}</td>
                      <td className="px-4 py-3">
                        <span className="rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                          {t.plano}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded px-2 py-0.5 text-xs font-medium ${statusColor(t.status)}`}>
                          {t.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {t.subscriptionAmount ? `R$ ${t.subscriptionAmount}` : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/gestor/tenants/${t.id}`)}
                          >
                            Detalhes
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setResendTarget(t)}
                          >
                            Reenviar
                          </Button>
                          {t.status === "active" && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => setActionTarget({ tenant: t, action: "suspend" })}
                            >
                              Suspender
                            </Button>
                          )}
                          {t.status === "suspended" && (
                            <Button
                              size="sm"
                              onClick={() => setActionTarget({ tenant: t, action: "reactivate" })}
                            >
                              Reativar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 20 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <span className="text-sm text-muted-foreground">{total} tenants</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
                  Anterior
                </Button>
                <Button variant="outline" size="sm" disabled={page * 20 >= total} onClick={() => setPage(page + 1)}>
                  Proximo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Action */}
      <ConfirmDialog
        open={!!actionTarget}
        onOpenChange={(open) => !open && setActionTarget(null)}
        title={actionTarget?.action === "suspend" ? "Suspender tenant" : "Reativar tenant"}
        description={
          actionTarget?.action === "suspend"
            ? `Tem certeza que deseja suspender "${actionTarget?.tenant.nome}"? O tenant perdera acesso ao sistema.`
            : `Reativar "${actionTarget?.tenant.nome}"? O tenant voltara a ter acesso ao sistema.`
        }
        confirmLabel={actionTarget?.action === "suspend" ? "Suspender" : "Reativar"}
        cancelLabel="Cancelar"
        variant={actionTarget?.action === "suspend" ? "danger" : "default"}
        loading={acting}
        onConfirm={handleAction}
      />

      {/* Confirm Resend Credentials */}
      <ConfirmDialog
        open={!!resendTarget}
        onOpenChange={(open) => !open && setResendTarget(null)}
        title="Reenviar credenciais"
        description={`Uma nova senha sera gerada e enviada por email para "${resendTarget?.email}". A senha anterior sera invalidada.`}
        confirmLabel="Reenviar"
        cancelLabel="Cancelar"
        variant="warning"
        loading={resending}
        onConfirm={handleResendCredentials}
      />
    </div>
  );
}
